import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import { api } from '@/shared/api/httpClient'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'

import { useAuth } from '../hooks/useAuth'
import { getAuthenticatedDestination } from '../utils/getAuthenticatedDestination'

const schema = z
  .object({
    newPassword: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres.'),
    confirmation: z.string().min(1, 'Confirma tu nueva contraseña.'),
  })
  .refine((value) => value.newPassword === value.confirmation, {
    message: 'Las contraseñas no coinciden.',
    path: ['confirmation'],
  })

type FormData = z.infer<typeof schema>

export function RequiredPasswordPage() {
  const navigate = useNavigate()
  const { user, markRequiredPasswordChanged } = useAuth()
  const [generalError, setGeneralError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmation: '' },
  })

  const submit = async ({ newPassword }: FormData) => {
    setGeneralError(null)
    try {
      await api.post('/auth/password/change-required', { newPassword })
      markRequiredPasswordChanged()
      void navigate(getAuthenticatedDestination(user?.role ?? null), { replace: true })
    } catch {
      setGeneralError('No se pudo guardar la contraseña. Intenta nuevamente.')
    }
  }

  return (
    <main className="bg-muted flex min-h-screen items-center justify-center px-4 py-8">
      <section className="bg-card w-full max-w-md space-y-6 rounded-xl border p-6 shadow-sm">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm">Primer acceso</p>
          <h1 className="font-serif text-2xl font-semibold">Cambiar contraseña</h1>
          <p className="text-muted-foreground text-sm">
            Define una contraseña personal para continuar al sistema.
          </p>
        </div>
        <form
          className="space-y-4"
          onSubmit={(event) => void handleSubmit(submit)(event)}
          noValidate
        >
          <div className="space-y-2">
            <Label htmlFor="required-new-password">Nueva contraseña *</Label>
            <Input
              id="required-new-password"
              type="password"
              autoComplete="new-password"
              {...register('newPassword')}
            />
            {errors.newPassword && (
              <p className="text-destructive text-sm" role="alert">
                {errors.newPassword.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="required-password-confirmation">Confirmar contraseña *</Label>
            <Input
              id="required-password-confirmation"
              type="password"
              autoComplete="new-password"
              {...register('confirmation')}
            />
            {errors.confirmation && (
              <p className="text-destructive text-sm" role="alert">
                {errors.confirmation.message}
              </p>
            )}
          </div>
          {generalError && (
            <p className="text-destructive text-sm" role="alert">
              {generalError}
            </p>
          )}
          <Button className="w-full" type="submit" disabled={isSubmitting}>
            Guardar contraseña
          </Button>
        </form>
      </section>
    </main>
  )
}
